import React, { FC } from 'react';
import {
  ArrayInput,
  FormDataConsumer,
  SelectInput,
  SimpleFormIterator,
  required,
  useTranslate,
} from 'react-admin';
import { useForm } from 'react-final-form';
import Button from '@material-ui/core/Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { l } from '~/utils/solutions';
import { useStyles } from '~/hooks/useSolutionsStyles';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import { useProductsBySolutionId } from '~/hooks/product';
import { H3 } from '~/components';
import { Solution } from '~/types';

interface PayrollFormProps {
  keyName: string;
  basePath: string;
  record: Solution;
}

export const PayrollForm: FC<PayrollFormProps> = ({ keyName, record }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));
  const form = useForm();

  const { data: handbookData } = useHandbooksQuery(record.id);
  const currencyChoices = (handbookData?.currencies ?? []).map((c) => ({ id: c.id, name: c.id }));

  const { data: products, isLoading: productsLoading } = useProductsBySolutionId(record.id);

  const validateHasEntries = (value: any[]) =>
    !value || value.length === 0 ? t('atLeastOneCurrency') : undefined;

  const validateNoDuplicate = (value: string, allValues: any) => {
    const rows = allValues?.[keyName]?.transitProducts ?? [];
    return rows.filter((r: any) => r?.currency === value).length > 1
      ? t('duplicateCurrency')
      : undefined;
  };

  return (
    <>
      <H3 topSpacing={0}>{t('transitProducts')}</H3>
      <ArrayInput
        source={`${keyName}.transitProducts`}
        label=''
        className={classes.simpleArrayInput}
        validate={validateHasEntries}
      >
        <SimpleFormIterator
          addButton={
            <Button startIcon={<AddCircleOutlineIcon />} color='default'>
              {t('addTransitProduct')}
            </Button>
          }
        >
          <FormDataConsumer>
            {({ getSource, scopedFormData }: any) => (
              <>
                <SelectInput
                  source={getSource('currency')}
                  label={t('currency')}
                  choices={currencyChoices}
                  validate={[required(), validateNoDuplicate]}
                  className={classes.inputWidth}
                  onChange={() => form.change(getSource('productId'), undefined)}
                />
                <SelectInput
                  source={getSource('productId')}
                  label={t('transitProduct')}
                  choices={
                    products
                      ?.filter((p) => p.currency === scopedFormData?.currency)
                      .map((p) => ({ id: p.id, name: p.name })) ?? []
                  }
                  validate={required()}
                  loading={productsLoading}
                  className={classes.inputWidth}
                />
              </>
            )}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};
